"use client";

import * as React from "react";
import { Icon } from "@/components/ui/icon";
import { Container } from "@/app/_components/layout/container";
import { protectionBlock } from "@/lib/content/homepage";
import { cn } from "@/lib/utils";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

const iconMap = {
  "circle-check": "check_circle",
  blocks: "widgets",
  eye: "visibility",
  lock: "lock",
} as const;

const toneClasses: Record<string, string> = {
  purple: "bg-clarus-purple",
  blue: "bg-clarus-blue",
  rose: "bg-clarus-rose",
  green: "bg-clarus-green",
};

export function ProtectionCards() {
  const gridRef = React.useRef<HTMLUListElement>(null);

  useIsomorphicLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const cards = Array.from(
      grid.querySelectorAll<HTMLLIElement>(":scope > li"),
    );
    if (cards.length === 0) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const isMobile = !window.matchMedia("(min-width: 768px)").matches;
    if (reduceMotion || isMobile) {
      cards.forEach((card) => {
        card.style.transform = "";
        card.style.opacity = "";
      });
      return;
    }

    let frame = 0;
    const update = () => {
      frame = 0;
      const vh = window.innerHeight;
      const startY = vh * 0.95;
      const endY = vh * 0.45;
      const maxOffset = window.innerWidth * 0.55;

      cards.forEach((card, i) => {
        const top = card.getBoundingClientRect().top;
        const raw = (startY - top) / (startY - endY);
        const p = Math.min(1, Math.max(0, raw));
        const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic

        if (p >= 1) {
          card.style.transition = "";
          card.style.transform = "";
          card.style.opacity = "";
          return;
        }

        const direction = i % 2 === 1 ? 1 : -1; // odd index = right column
        const x = direction * maxOffset * (1 - eased);
        card.style.transition = "none";
        card.style.transform = `translate3d(${x}px, 0, 0)`;
        card.style.opacity = String(eased);
      });
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update(); // set the correct start state before first paint
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section
      aria-label="Schutz"
      className="bg-white text-clarus-dark py-20 md:py-28 overflow-x-clip"
    >
      <Container>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-display-lg text-clarus-dark">
            {protectionBlock.heading}
          </h2>
          <p className="text-subline mt-4 text-clarus-dark/80">
            {protectionBlock.subline}
          </p>
        </div>

        <ul
          ref={gridRef}
          className="mt-14 md:mt-20 grid gap-6 md:grid-cols-2"
        >
          {protectionBlock.cards.map((card) => {
            const iconName = iconMap[card.icon as keyof typeof iconMap];
            return (
              <li
                key={card.title}
                className={cn(
                  toneClasses[card.tone] ?? "bg-white",
                  "rounded-card border-[1.5px] border-clarus-dark p-6 md:p-7 text-clarus-dark",
                  "transition-[transform,box-shadow] duration-200 ease-[ease] hover:-translate-y-1 hover:shadow-[4px_4px_0_0_var(--color-clarus-dark)] motion-reduce:transition-none motion-reduce:hover:translate-y-0",
                )}
              >
                <div className="flex items-start justify-between gap-4">
                  <h3
                    className="font-extrabold leading-tight text-clarus-dark"
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: "1.375rem",
                    }}
                  >
                    {card.title}
                  </h3>
                  {iconName ? (
                    <Icon
                      name={iconName}
                      className="mt-[3px] shrink-0 text-[24px] text-clarus-dark"
                    />
                  ) : null}
                </div>
                <p
                  className="mt-4 text-clarus-dark/85"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "0.9375rem",
                    lineHeight: 1.55,
                  }}
                >
                  {card.body}
                </p>
                <ul
                  className="mt-4 space-y-1.5 text-clarus-dark"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "0.9375rem",
                    lineHeight: 1.5,
                  }}
                >
                  {card.points.map((p) => (
                    <li key={p} className="flex items-baseline gap-2.5">
                      <span
                        aria-hidden="true"
                        className="text-clarus-dark leading-none text-[1.05em]"
                      >
                        •
                      </span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      </Container>
    </section>
  );
}
